import {
  assign,
  createActor,
  emit,
  enqueueActions,
  fromCallback,
  fromPromise,
  setup,
  waitFor,
} from 'xstate';

import { AppointmentNotAccessibleError, EluveError } from '@eluve/eluve-errors';
import {
  createDefaultLogActionFromCorrelatedLogger,
  logAction,
} from '@eluve/frontend-machine-utils';
import { CorrelatedLogger } from '@eluve/logger';
import { getMetadataFromFileName } from '@eluve/utils';

import {
  CallbackActorInvoke,
  SupportedAudioFormat,
  UploadFileEvents,
  UploadType,
} from './types';

/**
 * The name of a DOMException error thrown when a file is not found
 */
export const NOT_FOUND_ERROR_NAME = 'NotFoundError';

/**
 * Minimum file age before an OPFS NotFoundError is treated as terminal
 * (→ CancelOrphan). A missing OPFS file usually means the bytes are gone for
 * good — but around recording teardown the chunk's final encode + flush can
 * lag the Dexie row by well over the OPFS read-retry budget, so a young file
 * that fails to read is most likely still being written. Cancelling it would
 * permanently kill recoverable audio (the slot flips CANCELLED and the
 * transcript stays PENDING_AUDIO, blocking note generation). Younger files
 * are left in Dexie for the next periodic scan to retry instead. [ENG-4562]
 */
export const OPFS_NOT_FOUND_CANCEL_MIN_AGE_MS = 10 * 60 * 1000;

/**
 * True once the file is old enough that a terminal failure (missing OPFS
 * bytes, 403/404 from the upload endpoints) reflects reality rather than a
 * transient teardown window. Young files are deferred instead of cancelled:
 * around the Siloam login-page teardown the final OPFS flush can lag the
 * Dexie row, and the session wipe makes in-flight API calls fail with
 * 403/404 even though the OWNER still has full access — the row must survive
 * so the owner's next login can upload it. [ENG-4562]
 */
const isFileOldEnoughToCancel = (context: UploadFileMachineContext) => {
  const recordingStartedAtMs = Date.parse(context.recordingStartedAt);
  const fileAgeMs = Number.isNaN(recordingStartedAtMs)
    ? Number.POSITIVE_INFINITY
    : Date.now() - recordingStartedAtMs;
  return fileAgeMs >= OPFS_NOT_FOUND_CANCEL_MIN_AGE_MS;
};

const serializeUploadError = (error: unknown): Record<string, unknown> => {
  if (!(error instanceof Error)) {
    return { error: String(error) };
  }
  const details: Record<string, unknown> = {
    name: error.name,
    message: error.message,
    stack: error.stack,
  };
  if (error instanceof EluveError && error.metadata) {
    details.metadata = error.metadata;
  }
  const cause = (error as { cause?: unknown }).cause;
  if (cause instanceof Error) {
    details.cause = {
      name: cause.name,
      message: cause.message,
      stack: cause.stack,
    };
  } else if (cause !== undefined) {
    details.cause = cause;
  }
  return details;
};

export type UploadFileMachineInput = {
  fileName: string;
  tenantId: string;
  userId: string;
  appointmentId: string;
  uploadType: UploadType;
  format: SupportedAudioFormat;
  shouldBeTranscribed: boolean;
  wasDegraded: boolean;
  recordingStartedAt: string;
};

type GetFileDataArgs = Pick<
  UploadFileMachineInput,
  'fileName' | 'appointmentId' | 'tenantId'
>;

export type UploadFileMachineContext = UploadFileMachineInput & {
  file?: File;
  error?: Error | EluveError;
  /**
   * Set when the run ends in a failure/deferral that should NOT be reported
   * as success — the terminal Done state then skips its UPLOAD_FILE.DONE
   * emit (consumers like the dropped-audio recovery flow schedule note
   * generation on DONE). [ENG-4562]
   */
  failedTerminal?: boolean;
};

export type UploadFileArgs = {
  tenantId: string;
  appointmentId: string;
  fileId: string;
  file: File;
  uploadType: UploadType;
  format: SupportedAudioFormat;
  shouldBeTranscribed: boolean;
  wasDegraded: boolean;
  recordingStartedAt: string;
  chunkNum?: number;
};

export type GetFileDataFn = (args: GetFileDataArgs) => Promise<File>;
export type CleanupFileFn = (args: {
  appointmentId: string;
  fileId: string;
}) => Promise<void>;

type CancelOrphanedRowArgs = Pick<
  UploadFileMachineInput,
  'fileName' | 'appointmentId' | 'tenantId' | 'userId'
>;
export type CancelOrphanedRowFn = (
  args: CancelOrphanedRowArgs,
) => Promise<void>;

const getFileData = fromPromise<
  Awaited<ReturnType<GetFileDataFn>>,
  GetFileDataArgs
>(async () => {
  throw new Error('getFileData not implemented');
});

const cleanupFile = fromPromise<
  Awaited<ReturnType<CleanupFileFn>>,
  { fileId: string; appointmentId: string }
>(async () => {
  throw new Error('cleanupFile not implemented');
});

const cancelOrphanedRow = fromPromise<
  Awaited<ReturnType<CancelOrphanedRowFn>>,
  CancelOrphanedRowArgs
>(async () => {
  throw new Error('cancelOrphanedRow not implemented');
});

const uploadFile = fromCallback<UploadFileEvents, UploadFileArgs>(() => {
  throw new Error('uploadFile not implemented');
});

export const uploadFileMachine = setup({
  types: {
    context: {} as UploadFileMachineContext,
    input: {} as UploadFileMachineInput,
    events: {} as UploadFileEvents,
    emitted: {} as UploadFileEvents,
  },
  actors: {
    getFileData,
    uploadFile,
    cleanupFile,
    cancelOrphanedRow,
  },
  guards: {
    isRetryableError: ({ event, context }) => {
      if ('error' in event && event.error) {
        if (
          event.error instanceof DOMException &&
          event.error.name === NOT_FOUND_ERROR_NAME
        ) {
          // Only escalate a missing OPFS file to CancelOrphan once the file
          // is old enough that no finalize flush can still be in flight —
          // younger files stay in Dexie and the next periodic scan retries
          // them. [ENG-4562]
          return !isFileOldEnoughToCancel(context);
        }
      }
      return true;
    },
    isAppointmentInaccessibleError: ({ event, context }) => {
      if ('error' in event && event.error) {
        // A 403/404 on a YOUNG file is most likely the user-switch teardown
        // wiping the session out from under an in-flight upload — the row
        // must survive so the owner's next login can retry it. Only treat
        // inaccessibility as terminal for old files. [ENG-4562]
        return (
          event.error instanceof AppointmentNotAccessibleError &&
          isFileOldEnoughToCancel(context)
        );
      }
      return false;
    },
    hasFile: ({ context }) => Boolean(context?.file),
  },
  actions: {
    log: logAction,
    markFailedTerminal: assign({ failedTerminal: true }),
    onErrorGetFileData: enqueueActions(({ enqueue, event }) => {
      if (!('error' in event) || !event.error) return;
      enqueue({
        type: 'log',
        params: {
          level: 'warn',
          message: 'UploadFileMachine error',
          logMeta: {
            source: 'getFileData',
            ...serializeUploadError(event.error),
          },
        },
      });
      enqueue.assign({ error: event.error });
    }),
    onErrorUploadFile: enqueueActions(({ enqueue, event }) => {
      if (!('error' in event) || !event.error) return;
      enqueue({
        type: 'log',
        params: {
          level: 'warn',
          message: 'UploadFileMachine error',
          logMeta: {
            source: 'uploadFile',
            ...serializeUploadError(event.error),
          },
        },
      });
      enqueue.assign({ error: event.error });
    }),
    onErrorCleanupFile: enqueueActions(({ enqueue, event }) => {
      if (!('error' in event) || !event.error) return;
      enqueue({
        type: 'log',
        params: {
          level: 'warn',
          message: 'UploadFileMachine error',
          logMeta: {
            source: 'cleanupFile',
            ...serializeUploadError(event.error),
          },
        },
      });
      enqueue.assign({ error: event.error });
    }),
    onErrorCancelOrphanedRow: enqueueActions(({ enqueue, event }) => {
      if (!('error' in event) || !event.error) return;
      enqueue({
        type: 'log',
        params: {
          level: 'warn',
          message: 'UploadFileMachine error',
          logMeta: {
            source: 'cancelOrphanedRow',
            ...serializeUploadError(event.error),
          },
        },
      });
      enqueue.assign({ error: event.error });
    }),
  },
}).createMachine({
  /** @xstate-layout N4IgpgJg5mDOIC5QFcAOAbA9gQwgMQEt0wBZbAYwAsCA7MAOgHEwAXQ4gEWxewGIJMderQBumANYM0WXO1IVqQ5myJguPBKMzluBQQG0ADAF0jxxKFSZYBFnpoWQAD0QBGABwBmegBYAnJ7uAOwATD6eQQBshpFBADQgAJ5uYfRBniHuIaEArD6Rnq7ZAL7FCdI4+KpkVLQMynLqfGAATi2YLfQY3ABmHQC29BWy1Qp1TKyN3NiaNGI6dgYmZo5WNosOSM6IALSRIfQ5hp5+4fleIa6RkTkJyQgnfvTuBZmRfjnuruk+peUYlTkNUU9UmqiavFa7U63RYfRag2GVWIwPGDXB01m810S1MrnMWzWtnsjhcCC+3mihkM6XcOQKxz8d0QZ3ork8HJy9Mi-kiXhyfxASKBYyEAGFKGByOI5LwVoTrMTBKSWYU0uzPDlPIYfDTTvEkogQtd6LFrlE8idrp5BcLRrVxZLpbL9PjVoqNiqEEd3Oqgq4jldtTEfMyHn5DIcisafEE-KEihFbQCRijRQwAKop5FgXgZgAKABkAPIAQQ4AH08ABJQsAUXoBZL5erADlGPLLB6SVsye5dWyeSEdXkbiEQp4w+F6IEcpkgkEtYY-H5gr8ykLsyKHZmt6o80Wy5Wa-X6HhS7W6xxOyAiZ7e4hrc8Oa4-JlXD5h4Uw8acr5Ndq7z0lyOR+MmMg5qiQhZhBspNkeVaXmel4VvB5ZXjed49qAZJvq4bI+D4oHXF8y6ToaCDDvh+QBhyNwBD4-bgYC9ogvQYrENgNBoLKAhCFokhDHuaY7uxnHcagchYtoOI0GYmHdsqD4IH4rj4S8YRZP6hSeIxYb7PhhhXOkRzpOkJQbnaIlsRxYBcTx+5Qh0XToL0AxCbBrHjLZ9mSao0kLPY8kmO66zYdsCBqZ+9DGmEOrvMOXL6SEf6GEchhhBkJEcqUG40JgEBwI4VnyDuoVKpsOG7GpkZHCcZx8hkxlhjs06qbGGonIU-jMampVseinDTOV95VQgOzaocpmfqu2peDcYb9s8fg3JqmTLguAqWcJ-XeU6MqqCN4VkrqPjPPkunuFkwTarGYYfn+NxBP27inHkK7sr1kHpo2O1HUpY0FI9hEnEZNxaoE93jjFOTPa+XKBBcW3-J51neeJDnEP9lURVFZ2xougQfqujGhhRJxBGyX6nEGH66Ta22o7tQgcIIYDY16E21dNISzYY823BR0T4ZEVzXDy9LuPzkS5cUQA */
  id: 'uploadFileMachine',
  initial: 'GetFileData',
  context: ({ input }) => input,
  states: {
    GetFileData: {
      invoke: {
        src: 'getFileData',
        input: ({ context }) => ({
          appointmentId: context.appointmentId,
          fileName: context.fileName,
          tenantId: context.tenantId,
        }),
        onDone: {
          actions: assign({
            file: (context) => context.event.output,
          }),
          target: 'CheckFile',
        },
        onError: [
          {
            // Retryable (transient errors, or a YOUNG NotFound whose finalize
            // flush may still be in flight): leave the row for the next scan
            // and report FAILURE — emitting DONE here would let consumers
            // (e.g. the dropped-audio recovery flow) treat a deferred upload
            // as complete and schedule note generation without the audio.
            // [ENG-4562]
            target: 'Done',
            actions: [
              'onErrorGetFileData',
              'markFailedTerminal',
              emit(({ event }) => ({
                type: 'UPLOAD_FILE.FAILED' as const,
                error: event.error as Error,
              })),
            ],
            guard: 'isRetryableError',
          },
          {
            // NotFoundError after the cockatiel-wrapped getFile retries are
            // exhausted means the bytes are genuinely unrecoverable — escalate
            // to the backend cancel before tearing down local state. [ENG-4024]
            target: 'CancelOrphan',
            actions: ['onErrorGetFileData'],
          },
        ],
      },
    },
    CancelOrphan: {
      invoke: {
        src: 'cancelOrphanedRow',
        input: ({ context }) => ({
          fileName: context.fileName,
          appointmentId: context.appointmentId,
          tenantId: context.tenantId,
          userId: context.userId,
        }),
        // The callback already swallows API errors and logs them; this onError
        // exists only to handle truly unexpected throws so the local row still
        // gets torn down.
        onDone: { target: 'CleanupFile' },
        onError: {
          target: 'CleanupFile',
          actions: ['onErrorCancelOrphanedRow'],
        },
      },
    },
    CheckFile: {
      always: [
        {
          target: 'UploadFile',
          guard: 'hasFile',
        },
        {
          target: 'Done',
        },
      ],
    },
    UploadFile: {
      invoke: {
        src: 'uploadFile',
        input: ({ context }) => {
          const { file, fileName, userId, ...rest } = context;
          const { databaseFileId } = getMetadataFromFileName({
            fileName,
            userId,
          });

          return {
            // This was verified in the CheckFile state
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            file: file!,
            fileId: databaseFileId,
            ...rest,
          };
        },
      },
      on: {
        'UPLOAD_FILE.UPLOADING': {
          actions: [emit(({ event }) => event)],
        },
        'UPLOAD_FILE.FAILED': [
          {
            // 403/404 from prepareAppointmentFileUpload means the
            // (tenantId, appointmentId) pair will never succeed for this
            // caller. Tear down IDB + OPFS so the drift reconciler does not
            // resurrect the row.
            target: 'CancelOrphan',
            guard: 'isAppointmentInaccessibleError',
            actions: ['onErrorUploadFile'],
          },
          {
            target: 'Done',
            actions: [
              'onErrorUploadFile',
              'markFailedTerminal',
              emit(({ event }) => event),
            ],
          },
        ],
        'UPLOAD_FILE.FILE_UPLOADED': {
          target: 'CleanupFile',
          actions: [emit(({ event }) => event)],
        },
      },
    },
    CleanupFile: {
      invoke: {
        src: 'cleanupFile',
        input: ({ context }) => ({
          appointmentId: context.appointmentId,
          fileId: context.fileName,
        }),
        onDone: 'Done',
        onError: {
          target: 'Done',
          actions: ['onErrorCleanupFile'],
        },
      },
    },
    Done: {
      type: 'final',
      entry: [
        // Only report success when the run actually succeeded (or was a
        // benign no-op) — failure/deferral paths emitted UPLOAD_FILE.FAILED
        // on their way here. [ENG-4562]
        enqueueActions(({ enqueue, context }) => {
          if (!context.failedTerminal) {
            enqueue.emit({ type: 'UPLOAD_FILE.DONE' });
          }
        }),
      ],
    },
  },
});

export type UploadFileMachineOptions = {
  getFile: GetFileDataFn;
  uploadFile: CallbackActorInvoke<UploadFileArgs, UploadFileEvents>;
  cleanupFile: CleanupFileFn;
  cancelOrphanedRow: CancelOrphanedRowFn;
  logger: CorrelatedLogger;
  getLogAttributes?: (input: UploadFileMachineInput) => Record<string, unknown>;
};

export const createUploadFileMachineLogic = (
  args: UploadFileMachineOptions & { logAttributes?: Record<string, unknown> },
) => {
  const {
    getFile,
    uploadFile,
    cleanupFile,
    cancelOrphanedRow,
    logger,
    logAttributes,
  } = args;
  return uploadFileMachine.provide({
    actions: {
      log: createDefaultLogActionFromCorrelatedLogger(logger, logAttributes),
    },
    actors: {
      getFileData: fromPromise(({ input }) => getFile(input)),
      uploadFile: fromCallback(({ input, sendBack, emit }) =>
        uploadFile({
          input,
          sendBack: (evt) => {
            sendBack(evt);
          },
          emit,
        }),
      ),
      cleanupFile: fromPromise(
        async ({ input: { fileId, appointmentId } }) =>
          await cleanupFile({ fileId, appointmentId }),
      ),
      cancelOrphanedRow: fromPromise(
        async ({ input }) => await cancelOrphanedRow(input),
      ),
    },
  });
};

export const convertUploadFileMachineToCallback = (
  args: UploadFileMachineOptions,
) => {
  const wrapper: CallbackActorInvoke<
    UploadFileMachineInput,
    UploadFileEvents
  > = ({ input, sendBack: parentSendback, emit }) => {
    const logAttributes = args.getLogAttributes?.(input);
    const actor = createActor(
      createUploadFileMachineLogic({ ...args, logAttributes }),
      { input },
    );

    const onEvent = actor.on('*', (e) => {
      parentSendback(e);
      emit(e);
    });

    (async () => {
      try {
        actor.start();

        await waitFor(actor, (state) => state.status === 'done');
      } catch (e) {
        parentSendback({ type: 'UPLOAD_FILE.FAILED', error: e as Error });
      }
    })();

    return () => {
      onEvent.unsubscribe();
    };
  };

  return wrapper;
};
